import React, { useState } from 'react';
import { CheckCircle2, XCircle, AlertTriangle, ChevronDown, ChevronUp, Download } from 'lucide-react';

const DetailedResults = ({ results }) => {
  const [showSuccessful, setShowSuccessful] = useState(false);
  const [showFailed, setShowFailed] = useState(true);
  const [showErrors, setShowErrors] = useState(true);
  const [showCreatedInvoices, setShowCreatedInvoices] = useState(true);

  if (!results) return null;

  const downloadReport = () => {
    const reportData = {
      summary: results.summary,
      successful: results.successfulPayments,
      failed: results.failedPayments,
      errors: results.errorMessages,
      timestamp: new Date().toISOString()
    };

    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payment-report-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-green-50 rounded-lg p-4 text-center border border-green-200">
          <CheckCircle2 className="w-8 h-8 text-green-600 mx-auto mb-2" />
          <p className="text-2xl font-bold text-green-600">{results.paymentsCreated}</p>
          <p className="text-sm text-green-700">Successful</p>
        </div>
        <div className="bg-red-50 rounded-lg p-4 text-center border border-red-200">
          <XCircle className="w-8 h-8 text-red-600 mx-auto mb-2" />
          <p className="text-2xl font-bold text-red-600">{results.paymentsFailed}</p>
          <p className="text-sm text-red-700">Failed</p>
        </div>
        <div className="bg-yellow-50 rounded-lg p-4 text-center border border-yellow-200">
          <AlertTriangle className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
          <p className="text-2xl font-bold text-yellow-600">{results.rateLimitHits}</p>
          <p className="text-sm text-yellow-700">Rate Limits</p>
        </div>
        <div className="bg-blue-50 rounded-lg p-4 text-center border border-blue-200">
          <div className="text-2xl font-bold text-blue-600">{results.summary?.successRate}%</div>
          <p className="text-sm text-blue-700">Success Rate</p>
        </div>
      </div>

      {/* Download Report Button */}
      <div className="text-center">
        <button
          onClick={downloadReport}
          className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center gap-2 mx-auto"
        >
          <Download className="w-4 h-4" />
          Download Detailed Report
        </button>
      </div>

      {/* Created Invoices */}
      {results.createdInvoiceDetails?.length > 0 && (
        <div className="bg-blue-50 rounded-lg border border-blue-200">
          <button
            onClick={() => setShowCreatedInvoices(!showCreatedInvoices)}
            className="w-full p-4 text-left flex justify-between items-center hover:bg-blue-100 transition-colors"
          >
            <h3 className="text-lg font-semibold text-blue-800">
              📄 Newly Created Invoices ({results.createdInvoiceDetails.length})
            </h3>
            {showCreatedInvoices ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          
          {showCreatedInvoices && (
            <div className="px-4 pb-4">
              <div className="bg-white rounded border max-h-60 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-blue-100">
                    <tr>
                      <th className="text-left p-2 border-b">Invoice Number</th>
                      <th className="text-left p-2 border-b">Amount</th>
                      <th className="text-left p-2 border-b">Invoice ID</th>
                      <th className="text-left p-2 border-b">Account</th>
                      <th className="text-left p-2 border-b">Tax</th>
                      <th className="text-left p-2 border-b">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.createdInvoiceDetails.map((invoice, index) => (
                      <tr key={index} className="hover:bg-blue-50">
                        <td className="p-2 border-b font-mono text-blue-600">{invoice.invoiceNumber}</td>
                        <td className="p-2 border-b font-semibold">${invoice.amount.toFixed(2)}</td>
                        <td className="p-2 border-b font-mono text-xs text-gray-600">{invoice.invoiceId}</td>
                        <td className="p-2 border-b">{invoice.accountCode}</td>
                        <td className="p-2 border-b">{invoice.taxSetting}</td>
                        <td className="p-2 border-b">
                          <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
                            {invoice.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-3 p-3 bg-blue-100 rounded text-sm">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <strong>Total Created:</strong> {results.createdInvoiceDetails.length} invoices
                  </div>
                  <div>
                    <strong>Total Amount:</strong> ${results.createdInvoiceDetails.reduce((sum, inv) => sum + inv.amount, 0).toFixed(2)}
                  </div>
                  <div>
                    <strong>Account Used:</strong> 400 (Sales)
                  </div>
                  <div>
                    <strong>Tax Applied:</strong> None
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Successful Payments */}
      {results.successfulPayments?.length > 0 && (
        <div className="bg-green-50 rounded-lg border border-green-200">
          <button
            onClick={() => setShowSuccessful(!showSuccessful)}
            className="w-full p-4 text-left flex justify-between items-center hover:bg-green-100 transition-colors"
          >
            <h3 className="text-lg font-semibold text-green-800">
              ✅ Successful Payments ({results.successfulPayments.length})
            </h3>
            {showSuccessful ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          
          {showSuccessful && (
            <div className="px-4 pb-4">
              <div className="bg-white rounded border max-h-60 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-green-100">
                    <tr>
                      <th className="p-2 text-left">Invoice ID</th>
                      <th className="p-2 text-left">Amount</th>
                      <th className="p-2 text-left">Reference</th>
                      <th className="p-2 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.successfulPayments.map((payment, index) => (
                      <tr key={index} className="border-t hover:bg-green-50">
                        <td className="p-2 font-mono text-xs">{payment.invoiceId.substring(0, 20)}...</td>
                        <td className="p-2 font-semibold">${payment.amount.toFixed(2)}</td>
                        <td className="p-2">{payment.reference}</td>
                        <td className="p-2">
                          <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
                            {payment.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Failed Payments */}
      {results.failedPayments?.length > 0 && (
        <div className="bg-red-50 rounded-lg border border-red-200">
          <button
            onClick={() => setShowFailed(!showFailed)}
            className="w-full p-4 text-left flex justify-between items-center hover:bg-red-100 transition-colors"
          >
            <h3 className="text-lg font-semibold text-red-800">
              ❌ Failed Payments ({results.failedPayments.length})
            </h3>
            {showFailed ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          
          {showFailed && (
            <div className="px-4 pb-4">
              <div className="bg-white rounded border max-h-60 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-red-100">
                    <tr>
                      <th className="p-2 text-left">Invoice ID</th>
                      <th className="p-2 text-left">Amount</th>
                      <th className="p-2 text-left">Reference</th>
                      <th className="p-2 text-left">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.failedPayments.map((payment, index) => (
                      <tr key={index} className="border-t hover:bg-red-50">
                        <td className="p-2 font-mono text-xs">{payment.invoiceId.substring(0, 20)}...</td>
                        <td className="p-2 font-semibold">${payment.amount.toFixed(2)}</td>
                        <td className="p-2">{payment.reference}</td>
                        <td className="p-2">
                          <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs">
                            {payment.errorMessage.substring(0, 50)}...
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Error Messages */}
      {results.errorMessages?.length > 0 && (
        <div className="bg-yellow-50 rounded-lg border border-yellow-200">
          <button
            onClick={() => setShowErrors(!showErrors)}
            className="w-full p-4 text-left flex justify-between items-center hover:bg-yellow-100 transition-colors"
          >
            <h3 className="text-lg font-semibold text-yellow-800">
              ⚠️ Error Messages ({results.errorMessages.length})
            </h3>
            {showErrors ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          
          {showErrors && (
            <div className="px-4 pb-4">
              <div className="bg-white rounded border max-h-40 overflow-y-auto">
                <ul className="p-2 space-y-1">
                  {results.errorMessages.map((error, index) => (
                    <li key={index} className="text-sm text-gray-700 border-b pb-1">
                      {error}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Processing Summary */}
      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
        <h3 className="text-lg font-semibold text-blue-800 mb-3">Processing Summary</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p><strong>Total Attempted:</strong> {results.summary?.totalAttempted || 0}</p>
            <p><strong>Successfully Created:</strong> {results.paymentsCreated}</p>
            <p><strong>Failed:</strong> {results.paymentsFailed}</p>
          </div>
          <div>
            <p><strong>Rate Limit Hits:</strong> {results.rateLimitHits}</p>
            <p><strong>Success Rate:</strong> {results.summary?.successRate}%</p>
            <p><strong>Total Processed:</strong> {results.totalProcessed}</p>
          </div>
        </div>
      </div>

      {/* Recommendations */}
      {results.paymentsFailed > 0 && (
        <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
          <h3 className="text-lg font-semibold text-orange-800 mb-2">💡 Recommendations</h3>
          <ul className="text-sm text-orange-700 space-y-1">
            {results.rateLimitHits > 0 && (
              <li>• Rate limiting detected - the system will automatically retry failed payments</li>
            )}
            {results.paymentsFailed > 0 && (
              <li>• Review failed payments above for specific error details</li>
            )}
            <li>• Check your Xero account to verify successful payments were created</li>
            <li>• Download the detailed report for your records</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default DetailedResults;
